import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from '@/context/AuthContext'
import { apiFetch } from '@/lib/apiFetch'
import { cn } from '@/lib/utils'
import MagnumSays from '@/components/MagnumSays'
import TvPauseAutoOff from '@/components/TvPauseAutoOff'

interface Tv {
  id: number
  familyId: number
  guid: string
  name: string
  tvType: string
  mode: string
  autoOffOverrideUntil: number | null
  apiKeyPrefix: string | null
  lastSeenAt: number | null
  createdAt: number
}

interface FamilyScheduleWindow {
  id: number
  familyId: number
  dayOfWeek: number
  startMinute: number
  endMinute: number
  periodType: string
}

type PeriodType = 'UNLIMITED' | 'DAILY_ALLOWANCE' | 'BONUS_ONLY' | 'NEVER'

const PERIOD_TYPES: { value: PeriodType; label: string; shortLabel: string; bg: string; border: string; text: string; darkText: string }[] = [
  { value: 'DAILY_ALLOWANCE', label: 'Daily Allowance', shortLabel: 'Allowance', bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-700', darkText: 'dark:text-blue-400' },
  { value: 'BONUS_ONLY', label: 'Bonus Only', shortLabel: 'Bonus', bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-700', darkText: 'dark:text-amber-400' },
  { value: 'UNLIMITED', label: 'Unlimited', shortLabel: 'Free', bg: 'bg-green-500/20', border: 'border-green-500/40', text: 'text-green-700', darkText: 'dark:text-green-400' },
  { value: 'NEVER', label: 'Never', shortLabel: 'Off', bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-700', darkText: 'dark:text-red-400' },
]

function getPeriodStyle(periodType: string) {
  return PERIOD_TYPES.find(t => t.value === periodType) || PERIOD_TYPES[0]
}

const MODES = [
  { value: 'ALLOW', label: 'Always On', desc: 'TV can stay on anytime' },
  { value: 'DENY', label: 'Off', desc: 'TV off unless kid redeems time' },
  { value: 'SCHEDULE', label: 'Schedule', desc: 'Uses family schedule periods' },
]

function formatTime(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const period = h >= 12 ? 'PM' : 'AM'
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`
}

const START_HOUR = 6
const END_HOUR = 23
const TOTAL_ROWS = (END_HOUR - START_HOUR) * 2
const ROW_PX = 20
const SNAP = 30

function clampMins(m: number) { return Math.max(START_HOUR * 60, Math.min(END_HOUR * 60, m)) }
function snapMins(m: number) { return Math.round(m / SNAP) * SNAP }

function mergeWindows(windows: { dayOfWeek: number; startMinute: number; endMinute: number; periodType: string }[]) {
  const byKey: Record<string, { s: number; e: number }[]> = {}
  windows.forEach(w => {
    const key = `${w.dayOfWeek}:${w.periodType}`
    if (!byKey[key]) byKey[key] = []
    byKey[key].push({ s: w.startMinute, e: w.endMinute })
  })
  const result: { dayOfWeek: number; startMinute: number; endMinute: number; periodType: string }[] = []
  Object.entries(byKey).forEach(([key, ivs]) => {
    const [day, periodType] = key.split(':')
    ivs.sort((a, b) => a.s - b.s)
    const merged = [ivs[0]]
    for (let i = 1; i < ivs.length; i++) {
      const last = merged[merged.length - 1]
      if (ivs[i].s <= last.e) { last.e = Math.max(last.e, ivs[i].e) }
      else merged.push(ivs[i])
    }
    merged.forEach(iv => result.push({ dayOfWeek: Number(day), startMinute: iv.s, endMinute: iv.e, periodType }))
  })
  return result
}

/** "13:30" <-> minutes past midnight, for <input type="time">. */
function minutesToInput(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

function inputToMinutes(v: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(v)
  if (!match) return null
  const mins = Number(match[1]) * 60 + Number(match[2])
  return Number.isFinite(mins) ? mins : null
}

/**
 * One window as editable times. Typing and dragging both write to the same
 * `windows` state, so the grid above and this list can never disagree.
 * Commits on blur rather than on every keystroke, which would save partial times.
 */
function ScheduleWindowRow({ win, index, onChange, onRemove }: {
  win: FamilyScheduleWindow
  index: number
  onChange: (index: number, patch: { startMinute?: number; endMinute?: number; periodType?: string }) => void
  onRemove: (index: number) => void
}) {
  const [start, setStart] = useState(minutesToInput(win.startMinute))
  const [end, setEnd] = useState(minutesToInput(win.endMinute))

  // Re-sync when a drag on the grid changes this window underneath us.
  useEffect(() => { setStart(minutesToInput(win.startMinute)) }, [win.startMinute])
  useEffect(() => { setEnd(minutesToInput(win.endMinute)) }, [win.endMinute])

  const commitStart = () => {
    const mins = inputToMinutes(start)
    if (mins == null) { setStart(minutesToInput(win.startMinute)); return }
    const clamped = Math.min(clampMins(mins), win.endMinute - SNAP)
    onChange(index, { startMinute: clamped })
  }

  const commitEnd = () => {
    const mins = inputToMinutes(end)
    if (mins == null) { setEnd(minutesToInput(win.endMinute)); return }
    const clamped = Math.max(clampMins(mins), win.startMinute + SNAP)
    onChange(index, { endMinute: clamped })
  }

  const style = getPeriodStyle(win.periodType)
  const dayLabel = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][win.dayOfWeek]

  return (
    <div className="flex flex-wrap items-center gap-2 py-1.5 border-b border-border/40 last:border-b-0">
      <span className="w-9 shrink-0 text-[11px] font-semibold text-muted-foreground">{dayLabel}</span>
      <input
        type="time"
        step={SNAP * 60}
        value={start}
        onChange={(e) => setStart(e.target.value)}
        onBlur={commitStart}
        className="rounded border border-border bg-background px-1.5 py-1 text-xs min-h-[32px]"
        aria-label={`${dayLabel} start time`}
      />
      <span className="text-[11px] text-muted-foreground">until</span>
      <input
        type="time"
        step={SNAP * 60}
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        onBlur={commitEnd}
        className="rounded border border-border bg-background px-1.5 py-1 text-xs min-h-[32px]"
        aria-label={`${dayLabel} end time`}
      />
      <select
        value={win.periodType}
        onChange={(e) => onChange(index, { periodType: e.target.value })}
        className={cn('rounded border px-1.5 py-1 text-[11px] font-semibold min-h-[32px]', style.bg, style.border, style.text, style.darkText)}
        aria-label={`${dayLabel} period type`}
      >
        {PERIOD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <button
        onClick={() => onRemove(index)}
        className="ml-auto px-2 text-xs text-muted-foreground hover:text-red-500 min-h-[32px]"
        aria-label={`Remove ${dayLabel} window`}
      >Remove</button>
    </div>
  )
}

function FamilyScheduleEditor({ familyId }: { familyId: number }) {
  const [windows, setWindows] = useState<FamilyScheduleWindow[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTool, setActiveTool] = useState<PeriodType>('DAILY_ALLOWANCE')
  const gridRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ dayOfWeek: number; startMins: number } | null>(null)
  const [dragState, setDragState] = useState<{ dayOfWeek: number; startMins: number; endMins: number } | null>(null)

  useEffect(() => {
    apiFetch(`/tvs/family/${familyId}/schedule`).then(setWindows).finally(() => setLoading(false))
  }, [familyId])

  const save = async (updated: { dayOfWeek: number; startMinute: number; endMinute: number; periodType: string }[]) => {
    const merged = mergeWindows(updated)
    const result = await apiFetch(`/tvs/family/${familyId}/schedule`, {
      method: 'PUT',
      body: JSON.stringify({ windows: merged.map(w => ({ dayOfWeek: w.dayOfWeek, startMinute: w.startMinute, endMinute: w.endMinute, periodType: w.periodType })) }),
    })
    setWindows(result)
  }

  const yToMins = useCallback((clientY: number, colEl: Element) => {
    const rect = colEl.getBoundingClientRect()
    const raw = START_HOUR * 60 + ((clientY - rect.top) / ROW_PX) * 30
    return clampMins(snapMins(raw))
  }, [])

  const handleMouseDown = useCallback((dayOfWeek: number, e: React.MouseEvent) => {
    if (e.button !== 0) return
    if ((e.target as HTMLElement).closest('[data-sched-block]')) return
    e.preventDefault()
    const mins = yToMins(e.clientY, e.currentTarget)
    setDragState({ dayOfWeek, startMins: mins, endMins: mins + 60 })
    dragRef.current = { dayOfWeek, startMins: mins }
  }, [yToMins])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragRef.current) return
    const cols = gridRef.current?.querySelectorAll('[data-day-col]')
    if (!cols) return
    let found: Element | null = null
    cols.forEach(col => {
      const r = col.getBoundingClientRect()
      if (e.clientX >= r.left && e.clientX < r.right) found = col
    })
    if (!found) return
    const mins = yToMins(e.clientY, found)
    const { startMins } = dragRef.current
    setDragState(prev => prev ? {
      ...prev,
      startMins: Math.min(startMins, mins),
      endMins: Math.max(startMins, mins) + (Math.max(startMins, mins) === Math.min(startMins, mins) ? 30 : 0),
    } : null)
  }, [yToMins])

  const handleMouseUp = useCallback(() => {
    if (!dragState || !dragRef.current) { dragRef.current = null; setDragState(null); return }
    const { dayOfWeek, startMins, endMins } = dragState
    const newWindow = { dayOfWeek, startMinute: startMins, endMinute: Math.max(endMins, startMins + 30), periodType: activeTool }
    save([...windows.map(w => ({ dayOfWeek: w.dayOfWeek, startMinute: w.startMinute, endMinute: w.endMinute, periodType: w.periodType })), newWindow])
    dragRef.current = null
    setDragState(null)
  }, [dragState, windows, activeTool])

  const patchWindow = (index: number, patch: { startMinute?: number; endMinute?: number; periodType?: string }) => {
    save(windows.map((w, i) => ({
      dayOfWeek: w.dayOfWeek,
      startMinute: i === index ? patch.startMinute ?? w.startMinute : w.startMinute,
      endMinute: i === index ? patch.endMinute ?? w.endMinute : w.endMinute,
      periodType: i === index ? patch.periodType ?? w.periodType : w.periodType,
    })))
  }

  const removeWindow = (index: number) => {
    save(windows.filter((_, i) => i !== index).map(w => ({ dayOfWeek: w.dayOfWeek, startMinute: w.startMinute, endMinute: w.endMinute, periodType: w.periodType })))
  }

  const cycleWindowType = (index: number) => {
    const w = windows[index]
    const currentIdx = PERIOD_TYPES.findIndex(t => t.value === w.periodType)
    const nextType = PERIOD_TYPES[(currentIdx + 1) % PERIOD_TYPES.length].value
    save(windows.map((win, i) => ({
      dayOfWeek: win.dayOfWeek,
      startMinute: win.startMinute,
      endMinute: win.endMinute,
      periodType: i === index ? nextType : win.periodType,
    })))
  }

  const hourLabels: { top: number; label: string }[] = []
  for (let h = START_HOUR; h <= END_HOUR; h++) {
    const top = (h - START_HOUR) * 2 * ROW_PX
    hourLabels.push({ top, label: h === 12 ? '12pm' : h > 12 ? `${h - 12}pm` : `${h}am` })
  }

  const dayColumns = [
    { dayOfWeek: 1, label: 'Mon' }, { dayOfWeek: 2, label: 'Tue' }, { dayOfWeek: 3, label: 'Wed' },
    { dayOfWeek: 4, label: 'Thu' }, { dayOfWeek: 5, label: 'Fri' }, { dayOfWeek: 6, label: 'Sat' },
    { dayOfWeek: 7, label: 'Sun' },
  ]

  if (loading) return <p className="text-xs text-muted-foreground py-2">Loading schedule...</p>

  return (
    <div className="border border-border rounded-xl overflow-hidden select-none">
      <div className="px-3 py-2 border-b border-border">
        <h2 className="text-sm font-bold">Family Screen Time Schedule</h2>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Select a period type, then drag on the grid, or set exact times in the list
          below — the two stay in sync. Click a block to change its type.
        </p>
        <p className="text-[11px] text-muted-foreground mt-1">
          <span className="font-semibold text-foreground">End times are the moment it stops.</span>{' '}
          3:00 PM–5:00 PM means screen time opens at 3:00 and closes at 5:00 — 5:00 itself
          is not included.
        </p>
      </div>

      {/* Period type selector */}
      <div className="px-3 py-2 border-b border-border flex gap-1.5 flex-wrap">
        {PERIOD_TYPES.map(t => (
          <button
            key={t.value}
            onClick={() => setActiveTool(t.value)}
            className={cn(
              'px-2.5 py-1 rounded-md text-[11px] font-semibold border transition',
              activeTool === t.value
                ? `${t.bg} ${t.border} ${t.text} ${t.darkText}`
                : 'border-border text-muted-foreground hover:border-border/80'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Day headers */}
      <div className="grid border-b border-border" style={{ gridTemplateColumns: '36px repeat(7, 1fr)' }}>
        <div />
        {dayColumns.map(d => (
          <div key={d.dayOfWeek} className="text-center text-[10px] py-1.5 font-semibold text-muted-foreground border-l border-border">
            {d.label}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div
        ref={gridRef}
        className="relative overflow-y-auto"
        style={{ maxHeight: 400 }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div className="grid" style={{ gridTemplateColumns: '36px repeat(7, 1fr)', height: TOTAL_ROWS * ROW_PX }}>
          {/* Hour labels */}
          <div className="relative border-r border-border/50">
            {hourLabels.map(({ top, label }) => (
              <div key={label} style={{ top: top - 6 }} className="absolute right-1 text-[9px] text-muted-foreground/60">
                {label}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {dayColumns.map(d => {
            const dayWindows = windows
              .map((w, i) => ({ ...w, idx: i }))
              .filter(w => w.dayOfWeek === d.dayOfWeek)
            const isDragging = dragState?.dayOfWeek === d.dayOfWeek

            return (
              <div
                key={d.dayOfWeek}
                data-day-col={d.dayOfWeek}
                className="relative border-l border-border/40 cursor-crosshair"
                onMouseDown={(e) => handleMouseDown(d.dayOfWeek, e)}
              >
                {/* Grid lines */}
                {Array.from({ length: TOTAL_ROWS }, (_, i) => (
                  <div
                    key={i}
                    style={{ top: i * ROW_PX, height: ROW_PX }}
                    className={cn('absolute inset-x-0 border-b', i % 2 === 1 ? 'border-border/15' : 'border-border/30')}
                  />
                ))}

                {/* Schedule blocks */}
                {dayWindows.map(w => {
                  const top = ((w.startMinute - START_HOUR * 60) / 30) * ROW_PX
                  const height = Math.max(ROW_PX, ((w.endMinute - w.startMinute) / 30) * ROW_PX)
                  const style = getPeriodStyle(w.periodType)
                  const showLabel = height >= ROW_PX * 2
                  return (
                    <div
                      key={w.idx}
                      style={{ top, height, left: 2, right: 2 }}
                      data-sched-block
                      title={`${style.shortLabel}: opens ${formatTime(w.startMinute)}, closes ${formatTime(w.endMinute)}`}
                      onClick={(e) => { e.stopPropagation(); cycleWindowType(w.idx) }}
                      className={cn('absolute rounded border text-[9px] font-medium overflow-hidden group cursor-pointer', style.bg, style.border, style.text, style.darkText)}
                    >
                      <div className="flex items-start justify-between px-1 pt-0.5 leading-tight">
                        <span className="truncate">
                          {showLabel && <>{style.shortLabel} · </>}
                          {formatTime(w.startMinute)}–{formatTime(w.endMinute)}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeWindow(w.idx) }}
                          className="shrink-0 ml-0.5 opacity-0 group-hover:opacity-100 hover:text-red-500 transition-opacity text-[10px]"
                        >✕</button>
                      </div>
                    </div>
                  )
                })}

                {/* Drag preview */}
                {isDragging && (() => {
                  const top = ((dragState.startMins - START_HOUR * 60) / 30) * ROW_PX
                  const height = Math.max(ROW_PX, ((Math.max(dragState.endMins, dragState.startMins + 30) - dragState.startMins) / 30) * ROW_PX)
                  const style = getPeriodStyle(activeTool)
                  return (
                    <div
                      style={{ top, height, left: 2, right: 2 }}
                      className={cn('absolute rounded border border-dashed pointer-events-none', style.bg, style.border)}
                    />
                  )
                })()}
              </div>
            )
          })}
        </div>
      </div>

      <div className="border-t border-border px-3 py-2">
        <p className="text-[11px] font-semibold text-muted-foreground mb-1">Exact times</p>
        {windows.length === 0 ? (
          <p className="py-2 text-[11px] text-muted-foreground">
            No windows yet — drag on the grid above to add one.
          </p>
        ) : (
          windows
            .map((w, i) => ({ w, i }))
            .sort((a, b) => a.w.dayOfWeek - b.w.dayOfWeek || a.w.startMinute - b.w.startMinute)
            .map(({ w, i }) => (
              <ScheduleWindowRow
                key={`${w.dayOfWeek}-${w.startMinute}-${w.endMinute}-${w.periodType}`}
                win={w}
                index={i}
                onChange={patchWindow}
                onRemove={removeWindow}
              />
            ))
        )}
      </div>
    </div>
  )
}

function TvCard({ tv, onUpdate, onDelete }: {
  tv: Tv
  onUpdate: () => void
  onDelete: () => void
}) {
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState(tv.name)
  const [saving, setSaving] = useState(false)
  const [showKey, setShowKey] = useState<string | null>(null)
  const [provisioning, setProvisioning] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [status, setStatus] = useState<{ authorized: boolean; summary: string; reason: string; minutesLeft?: number; childName?: string } | null>(null)

  useEffect(() => {
    const load = () => {
      apiFetch(`/tvs/${tv.id}/status`).then(setStatus).catch(() => {})
    }
    load()
    const interval = setInterval(load, 30000)
    return () => clearInterval(interval)
  }, [tv.id, tv.mode])

  const updateMode = async (mode: string) => {
    setSaving(true)
    try {
      await apiFetch(`/tvs/${tv.id}`, { method: 'PUT', body: JSON.stringify({ mode }) })
      onUpdate()
    } finally {
      setSaving(false)
    }
  }

  const saveName = async () => {
    if (!nameValue.trim() || nameValue.trim() === tv.name) {
      setEditingName(false)
      return
    }
    setSaving(true)
    try {
      await apiFetch(`/tvs/${tv.id}`, { method: 'PUT', body: JSON.stringify({ name: nameValue.trim() }) })
      setEditingName(false)
      onUpdate()
    } finally {
      setSaving(false)
    }
  }

  const provision = async () => {
    setProvisioning(true)
    try {
      const result = await apiFetch(`/tvs/${tv.id}/provision`, { method: 'POST' })
      setShowKey(result.apiKey)
      onUpdate()
    } finally {
      setProvisioning(false)
    }
  }

  const handleDelete = async () => {
    await apiFetch(`/tvs/${tv.id}`, { method: 'DELETE' })
    onDelete()
  }

  return (
    <div className="border border-border rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        {editingName ? (
          <div className="flex items-center gap-2">
            <input value={nameValue} onChange={e => setNameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveName()}
              className="px-2 py-1 border border-border rounded-lg bg-background text-sm font-semibold"
              autoFocus />
            <button onClick={saveName} disabled={saving}
              className="text-xs text-primary font-medium">Save</button>
            <button onClick={() => { setEditingName(false); setNameValue(tv.name) }}
              className="text-xs text-muted-foreground">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setEditingName(true)}
            className="text-sm font-semibold hover:text-primary transition">
            {tv.name}
          </button>
        )}
        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{tv.tvType.replace('_', ' ')}</span>
      </div>

      <div className="flex gap-1">
        {MODES.map(m => (
          <button key={m.value} onClick={() => updateMode(m.value)} disabled={saving}
            className={cn(
              'flex-1 py-1.5 rounded-lg border text-xs font-medium transition',
              tv.mode === m.value
                ? 'border-primary bg-primary/5 text-primary'
                : 'border-border text-muted-foreground hover:border-primary/50'
            )}>
            {m.label}
          </button>
        ))}
      </div>

      {tv.mode === 'SCHEDULE' && (
        <p className="text-xs text-muted-foreground">Uses the family schedule below — allowance, bonus, unlimited, and off periods.</p>
      )}

      {status && (
        <div className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium',
          status.authorized
            ? 'bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-400'
        )}>
          <span className={cn(
            'w-2 h-2 rounded-full shrink-0',
            status.authorized ? 'bg-green-500' : 'bg-red-500'
          )} />
          {status.summary}
        </div>
      )}

      <div className="py-2 border-t border-border">
        <TvPauseAutoOff tvId={tv.id} autoOffOverrideUntil={tv.autoOffOverrideUntil} onUpdate={onUpdate} />
      </div>

      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">WatchTimmy</p>
        {showKey ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
            <p className="text-xs font-bold text-amber-800">Save this API key — it won't be shown again!</p>
            <code className="text-xs break-all select-all block bg-white p-2 rounded border border-amber-200">{showKey}</code>
          </div>
        ) : tv.apiKeyPrefix ? (
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              Key: <code>{tv.apiKeyPrefix}...</code>
              {tv.lastSeenAt && (
                <> · Last seen {new Date(tv.lastSeenAt).toLocaleString()}</>
              )}
            </span>
            <button onClick={provision} disabled={provisioning}
              className="text-xs text-primary hover:underline">Regenerate Key</button>
          </div>
        ) : (
          <button onClick={provision} disabled={provisioning}
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-medium hover:opacity-90 transition disabled:opacity-50 min-h-[36px]">
            {provisioning ? 'Generating...' : 'Generate API Key'}
          </button>
        )}
      </div>

      <div className="flex justify-end pt-1">
        {confirmDelete ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-red-600">Delete this TV?</span>
            <button onClick={handleDelete}
              className="text-xs px-2 py-1 bg-red-500 text-white rounded-md font-medium">Yes</button>
            <button onClick={() => setConfirmDelete(false)}
              className="text-xs text-muted-foreground">Cancel</button>
          </div>
        ) : (
          <button onClick={() => setConfirmDelete(true)}
            className="text-xs text-red-400 hover:text-red-600">Delete</button>
        )}
      </div>
    </div>
  )
}

export default function TvManagementPage() {
  const { user } = useAuth()
  const [tvs, setTvs] = useState<Tv[]>([])
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const familyId = user?.type === 'parent' ? user.families?.[0]?.id : null

  const load = async () => {
    if (!familyId) return
    try {
      const data = await apiFetch(`/tvs?familyId=${familyId}`)
      setTvs(data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [familyId])

  const createTv = async () => {
    if (!newName.trim() || !familyId) return
    setCreating(true)
    try {
      await apiFetch('/tvs', {
        method: 'POST',
        body: JSON.stringify({ familyId, name: newName.trim(), tvType: 'GOOGLE_TV' }),
      })
      setNewName('')
      load()
    } finally {
      setCreating(false)
    }
  }

  if (!familyId) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <p className="text-muted-foreground">No family found. Create a family first.</p>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
      <div className="text-center">
        <h1 className="text-2xl font-black text-foreground">TVs</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your family's TVs and screen time controls</p>
      </div>

      <MagnumSays size="sm">
        Add your TVs here, then set up WatchTimmy on a Raspberry Pi to enforce the rules!
      </MagnumSays>

      <div className="flex gap-2">
        <input
          type="text"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && createTv()}
          placeholder="TV name (e.g. Living Room)"
          className="flex-1 px-3 py-2 border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
        <button
          onClick={createTv}
          disabled={creating || !newName.trim()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-bold hover:opacity-90 transition disabled:opacity-50 min-h-[44px]"
        >
          {creating ? 'Adding...' : 'Add TV'}
        </button>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground text-sm">Loading...</p>
      ) : tvs.length === 0 ? (
        <p className="text-center text-muted-foreground text-sm py-4">No TVs added yet.</p>
      ) : (
        <div className="space-y-4">
          <FamilyScheduleEditor familyId={familyId} />
          {tvs.map(tv => (
            <TvCard key={tv.id} tv={tv} onUpdate={load} onDelete={load} />
          ))}
        </div>
      )}
    </div>
  )
}
